/**
 * Portal Student Management API Client
 *
 * Handles CRUD operations for student management with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// Grade options (Kindy, Pre-primary, Year 1-12) — kept in sync with
// YEAR_OPTIONS in courseGroupPortalApi.js and YEARS in materialPortalApi.js
export const GRADUATED = 13;

export const GRADES = [
  { value: -1, label: 'Kindy' },
  { value: 0, label: 'Pre-primary' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `Year ${i + 1}`
  })),
  { value: GRADUATED, label: 'Graduated' }
];

// Status options
export const STUDENT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

// Gender options
export const GENDERS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' }
];

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/student-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List all students with optional filters.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {Object} filters - Optional filters
 * @param {string} filters.parentId - Filter by parent ID
 * @param {string} filters.campusId - Filter by campus ID
 * @param {number} filters.grade - Filter by grade
 * @param {string} filters.status - Filter by status ('active' or 'inactive')
 */
export const listStudents = async (filters = {}) => {
  const result = await apiRequest('list', { filters });
  return result.students || [];
};

/**
 * Get a single student by ID.
 *
 * @param {string} studentId - Student ID
 */
export const getStudent = async (studentId) => {
  const result = await apiRequest('get', { studentId });
  return result.student;
};

/**
 * Create a new student.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {Object} studentData - Student data
 * @param {string} studentData.name - Student name (required)
 * @param {string[]} studentData.campuses - Array of campus IDs (required, at least one)
 * @param {string} studentData.gender - Gender ('Male' or 'Female')
 * @param {number} studentData.grade - Grade (1-16)
 * @param {string} studentData.schoolName - School name
 * @param {string} studentData.status - Status ('active' or 'inactive')
 * @param {string} studentData.parentName - Parent name
 * @param {string} studentData.parentEmail - Parent email
 * @param {string} studentData.parentPhone - Parent phone
 * @param {string} studentData.parentAddress - Parent address
 * @param {string} studentData.additionalNotes - Additional notes
 */
export const createStudent = async (studentData) => {
  const result = await apiRequest('create', studentData);
  return result.student;
};

/**
 * Update a student.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {string} studentId - Student ID
 * @param {Object} updates - Fields to update
 * @param {string} updates.name - Student name
 * @param {string[]} updates.campuses - Array of campus IDs (at least one required)
 * @param {string} updates.gender - Gender ('Male' or 'Female')
 * @param {number} updates.grade - Grade (1-16)
 * @param {string} updates.schoolName - School name
 * @param {string} updates.status - Status ('active' or 'inactive')
 * @param {string} updates.parentName - Parent name
 * @param {string} updates.parentEmail - Parent email
 * @param {string} updates.parentPhone - Parent phone
 * @param {string} updates.parentAddress - Parent address
 * @param {string} updates.additionalNotes - Additional notes
 */
export const updateStudent = async (studentId, updates) => {
  const result = await apiRequest('update', { studentId, ...updates });
  return result.student;
};

/**
 * Delete a student.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {string} studentId - Student ID to delete
 */
export const deleteStudent = async (studentId) => {
  const result = await apiRequest('delete', { studentId });
  return result;
};

/** Grade +1 preview for one campus. Changes nothing. */
export const gradePromotionPreview = async (campusId, academicYear) => {
  return apiRequest('gradePromotionPreview', { campusId, academicYear });
};

/** Grade +1 for every student of one campus. Refused (409) if already run for academicYear. */
export const promoteGrades = async (campusId, academicYear) => {
  return apiRequest('promoteGrades', { campusId, academicYear });
};

/**
 * Get grade label from grade number.
 *
 * @param {number} grade - Grade number (-1 = Kindy, 0 = Pre-primary, 1-12 = Year N, 13 = Graduated)
 */
export const getGradeLabel = (grade) => {
  if (grade === null || grade === undefined || grade === '') return '';
  const n = typeof grade === 'string' ? parseInt(grade, 10) : grade;
  if (n === -1) return 'Kindy';
  if (n === 0) return 'Pre-primary';
  if (n === GRADUATED) return 'Graduated';
  return `Year ${n}`;
};

/**
 * Get status label and color.
 *
 * @param {string} status - Status value
 */
export const getStatusInfo = (status) => {
  switch (status) {
    case 'active':
      return { label: 'Active', color: 'green' };
    case 'inactive':
      return { label: 'Inactive', color: 'red' };
    default:
      return { label: status || 'Unknown', color: 'gray' };
  }
};

const studentPortalApi = {
  listStudents,
  getStudent,
  createStudent,
  updateStudent,
  deleteStudent,
  gradePromotionPreview,
  promoteGrades,
  getGradeLabel,
  getStatusInfo,
  GRADES,
  GRADUATED,
  STUDENT_STATUSES,
  GENDERS
};

export default studentPortalApi;
